import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
import type { Lang } from "@majourneys/shared";

import { CARD_FILL, CARD_RADIUS, cardFrameStyle } from "./card-frame";
import { ImageWithFallback } from "./image-with-fallback";
import { pewterInner } from "./pewter-edge";

/** Words a guest reads in a minute; the usual estimate for web prose. */
const WORDS_PER_MINUTE = 200;

/**
 * Minutes to read a post, or null when the list did not send enough to say.
 * The editor's (or API's) `readMinutes` wins; otherwise the body is counted,
 * tags stripped. Never a guess: no number and no body means no reading time.
 */
export function blogReadMinutes(post: { readMinutes?: number | null; content?: string | null }): number | null {
  if (typeof post.readMinutes === "number" && post.readMinutes > 0) return Math.round(post.readMinutes);
  const text = post.content?.replace(/<[^>]*>/g, " ").trim();
  if (!text) return null;
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
}

export interface BlogCardProps {
  lang: Lang;
  slug: string;
  title: string;
  image?: string;
  /** Category or city, whichever the post carries. */
  eyebrow?: string;
  readMinutes?: number | null;
  /** Heading level inside the card, so lists keep their outline. */
  as?: "h2" | "h3";
  className?: string;
}

/**
 * Pewter kit #35 (owner, 2026-10-09): the blog card. A pewter image card, the
 * category (or city) and reading time on top, a two-line title, nothing else.
 * The whole card is the link.
 */
export function BlogCard({ lang, slug, title, image, eyebrow, readMinutes, as: Heading = "h3", className }: BlogCardProps) {
  const { t } = useTranslation();
  const hasMinutes = typeof readMinutes === "number" && readMinutes > 0;
  return (
    <Link
      to="/$lang/blog/$slug"
      params={{ lang, slug }}
      className={`mj-activity-card group block transition-transform duration-200 ease-out hover:-translate-y-[3px] motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${className ?? ""}`}
      style={cardFrameStyle}
      data-testid="blog-card"
    >
      <div className="flex h-full flex-col overflow-hidden p-1.5" style={pewterInner(CARD_FILL, CARD_RADIUS)}>
        <div className="aspect-[16/10] overflow-hidden rounded-[12px]" style={{ background: "rgba(17,14,57,0.055)" }}>
          <ImageWithFallback src={image} alt={title} className="h-full w-full object-cover" />
        </div>
        <div className="px-2 pb-2 pt-2.5">
          {eyebrow || hasMinutes ? (
            <p className="flex min-w-0 flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#5a5d77]">
              {eyebrow ? <span className="min-w-0 break-words">{eyebrow}</span> : null}
              {eyebrow && hasMinutes ? (
                <span aria-hidden="true" className="h-1 w-1 shrink-0 rounded-full bg-[#5a5d77]" />
              ) : null}
              {hasMinutes ? (
                <span className="inline-flex shrink-0 items-center gap-1">
                  <Icon name="ri-time-line" className="text-[12px]" aria-hidden="true" />
                  {t("blog.min_read", { count: readMinutes })}
                </span>
              ) : null}
            </p>
          ) : null}
          <Heading className="mt-1 line-clamp-2 font-heading text-[15px] font-semibold leading-snug text-[#110E39]">
            {title}
          </Heading>
        </div>
      </div>
    </Link>
  );
}
